import { useState } from 'react';

import { Link } from 'react-router-dom';

import { Permission } from '../../../../../../../../../../backend/src/auth/permissions/permission';
import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { useDeleteUnprocessedDoc } from '@/api/mutations/useDeleteUnprocessedDoc';
import useUpdateDoc from '@/api/mutations/useUpdateDoc';
import { Doc } from '@/api/types/backend-dto-types';
import { useHasPermission } from '@/hooks/useHasPermission';
import { ConfirmationModal } from '@/new/components/dialogs/confirmation-dialog';
import { EditFieldDialog } from '@/new/components/edit-field-dialog';
import { Icon } from '@/new/components/icon';
import { OverflowMenu } from '@/new/components/overflow-menu';
import { getProcessingState, ProcessingState } from '@/new/components/processing-chip';
import { Button } from '@/new/components/shadcn/button';
import { DropdownMenuItem } from '@/new/components/shadcn/dropdown-menu';
import { Tabs, TabsList, TabsTrigger } from '@/new/components/shadcn/tabs';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/new/components/shadcn/tooltip';

type PanelMode = 'rawDoc' | 'details';

interface DocPanelHeaderProps {
  doc: Doc;
  matterId: string;
  subsetId: string;
  panelMode: PanelMode;
  setPanelMode: (mode: PanelMode) => void;
  onDeleted?: () => void;
}

export function DocPanelHeader({ doc, matterId, subsetId, panelMode, setPanelMode, onDeleted }: DocPanelHeaderProps) {
  const { hasPermission } = useHasPermission();
  const canUpdateDoc = hasPermission(Permission.DocumentUpdate);
  const canDeleteDoc = hasPermission(Permission.DocumentDelete);

  const { mutate: updateDocFileName } = useUpdateDoc();

  const isUnprocessed = getProcessingState(doc) === ProcessingState.NotProcessed;

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const { mutate: deleteUnprocessedDoc, isLoading: isDeleting } = useDeleteUnprocessedDoc(matterId, () => {
    setDeleteDialogOpen(false);
    onDeleted?.();
  });

  const handleConfirmDelete = () => {
    trackCustomEvent(TrackingEventsV2.DocumentDelete, { documentId: doc.id });
    deleteUnprocessedDoc(doc.id);
  };

  return (
    <div className="flex flex-col border-b bg-gray-50 p-3">
      <div className="flex items-center justify-between gap-4">
        <Tabs value={panelMode}>
          <TabsList variant="pill">
            <TabsTrigger value="rawDoc" onMouseDown={(e) => e.preventDefault()} onClick={() => setPanelMode('rawDoc')}>
              <Icon className="lucide--file-text" />
              Document
            </TabsTrigger>
            <TabsTrigger
              value="details"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => setPanelMode('details')}
            >
              <Icon className="lucide--info" />
              Details
            </TabsTrigger>
          </TabsList>
        </Tabs>

        <div className="flex items-center gap-1">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button asChild size="sm" variant="white" className="rounded-full">
                <Link to={`/matters/${matterId}/subsets/${subsetId}/docs/${doc.id}`} aria-label="View document facts">
                  View Full Doc
                  <Icon className="lucide--arrow-right size-3" />
                </Link>
              </Button>
            </TooltipTrigger>
            <TooltipContent>View document alongside its facts</TooltipContent>
          </Tooltip>

          {canDeleteDoc && isUnprocessed && (
            <OverflowMenu ariaLabel="More document actions">
              <DropdownMenuItem
                className="text-red-600 focus:bg-red-50 focus:text-red-700"
                disabled={isDeleting}
                onClick={() => setDeleteDialogOpen(true)}
              >
                <Icon className="lucide--trash-2" />
                Delete document
              </DropdownMenuItem>
            </OverflowMenu>
          )}
        </div>
      </div>

      <div className="px-2 pt-3">
        <div className="text-sm font-medium break-words text-gray-700">
          {doc.file.name}
          {canUpdateDoc && (
            <EditFieldDialog
              title="Edit document name"
              description="Update the file name for this document."
              fieldLabel="Document name"
              currentValue={doc.file.name}
              onSave={(value) => {
                const trimmed = value.trim();
                if (!trimmed || trimmed === doc.file.name) return;
                updateDocFileName({ matterId, docId: doc.id, field: 'filename', value: trimmed });
                trackCustomEvent(TrackingEventsV2.DocumentUpdateName, { documentId: doc.id });
              }}
              triggerClassName="ml-1.5 inline-flex h-5 w-5 -translate-y-px items-center justify-center align-middle px-0 py-0"
            />
          )}
        </div>
      </div>

      <ConfirmationModal
        isOpen={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Delete document?"
        onConfirm={handleConfirmDelete}
        isLoading={isDeleting}
      >
        <p className="text-sm text-gray-600">
          This will permanently delete <span className="font-medium text-gray-800">{doc.file.name}</span>. As this
          document is unprocessed, no extracted facts or other derived data will be affected. This action cannot be
          undone.
        </p>
      </ConfirmationModal>
    </div>
  );
}
